<template>
  <div class="home">
    <div class="container">
      <div class="hero-section">
        <h1>Welcome to ClipShare</h1>
        <p class="hero-description">A secure, simple way to share clipboard content and files between devices</p>
      </div>
      
      <div class="main-actions">
        <div class="create-clipboard">
          <h2>Create a New Clipboard</h2>
          <div class="form-group">
            <input 
              v-model="clipboardName" 
              placeholder="Enter clipboard name (optional)" 
              class="form-control"
            />
            <textarea 
              v-model="clipboardContent" 
              placeholder="Enter clipboard description (optional)" 
              class="form-control textarea-control"
              rows="3"
            ></textarea>
            <button @click="createClipboard" class="create-btn">Create Clipboard</button>
          </div>
        </div>
        
        <div class="or-divider">
          <span>OR</span>
        </div>
        
        <div class="access-clipboard">
          <h2>Access Existing Clipboard</h2>
          <div class="form-group">
            <input 
              v-model="clipboardId" 
              placeholder="Enter clipboard ID" 
              class="form-control"
            />
            <button @click="accessClipboard" class="access-btn">Access Clipboard</button>
          </div>
        </div>
      </div>
      
      <!-- Clipboard History Section -->
      <div class="clipboard-history" v-if="clipboardHistory.length > 0">
        <div class="history-header">
          <h2>Recent Clipboards</h2>
          <button @click="clearAllHistory" class="clear-history-btn" title="Clear all history">
            Clear All
          </button>
        </div>
        <div class="history-grid">
          <div 
            v-for="item in clipboardHistory" 
            :key="item.id" 
            class="history-item"
            @click="accessHistoryClipboard(item.id)"
          >
            <div class="history-icon">📋</div>
            <div class="history-content">
              <h3 class="history-title">{{ item.name }}</h3>
              <div class="history-meta">
                <span class="visit-time">
                  <span class="meta-icon">🕒</span>
                  {{ formatRelativeTime(item.lastVisited) }}
                </span>
                <span class="visit-count">
                  <span class="meta-icon">👁️</span>
                  {{ item.visitCount }} visit{{ item.visitCount === 1 ? '' : 's' }}
                </span>
              </div>
            </div>
            <button 
              @click.stop="removeFromHistory(item.id)" 
              class="remove-btn"
              title="Remove from history"
            >
              ×
            </button>
          </div>
        </div>
      </div>
      
      <div class="feature-highlights-section">
        <h2>Why Choose ClipShare?</h2>
        <div class="feature-highlights">
          <div class="feature-item">
            <div class="feature-icon">📋</div>
            <h3>Easy Sharing</h3>
            <p>Share text and files instantly with a simple link</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">🔒</div>
            <h3>Secure</h3>
            <p>Your data is protected with unique IDs and secure transfers</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">🌐</div>
            <h3>Cross-Platform</h3>
            <p>Works on any device with a web browser or command line</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">✨</div>
            <h3>Rich Preview</h3>
            <p>Format text with syntax highlighting and preview files instantly</p>
          </div>
        </div>
      </div>
      
      <div class="quick-links">
        <h2>Need Help?</h2>
        <div class="links-grid">
          <router-link to="/docs" class="link-card">
            <div class="link-icon">📚</div>
            <h3>Documentation</h3>
            <p>Complete guide for API usage and client tools</p>
          </router-link>
          
          <!-- <a href="/clipshare.sh" download class="link-card">
            <div class="link-icon">⬬</div>
            <h3>Download Client</h3>
            <p>Shell script for server-to-server file transfers</p>
          </a> -->
          
          <router-link to="/docs#examples" class="link-card">
            <div class="link-icon">🚀</div>
            <h3>Quick Examples</h3>
            <p>Common usage patterns and workflows</p>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { clipboardService } from '@/services/api';
import { historyService } from '@/services/historyService';

export default {
  name: 'Home',
  data() {
    return {
      clipboardName: '',
      clipboardContent: '',
      clipboardId: '',
      error: null,
      clipboardHistory: []
    }
  },
  mounted() {
    this.loadClipboardHistory();
  },
  methods: {
    async createClipboard() {
      try {
        const response = await clipboardService.createClipboard(
          this.clipboardName || 'Untitled Clipboard',
          this.clipboardContent || ''
        );
        
        const uniqueId = response.data.clipboard.unique_id;
        const clipboardName = this.clipboardName || 'Untitled Clipboard';
        
        // Add to history before navigation
        historyService.addToHistory(uniqueId, clipboardName);
        
        this.$router.push(`/${uniqueId}`);
      } catch (error) {
        console.error('Error creating clipboard:', error);
        this.error = 'Failed to create clipboard';
      }
    },
    
    accessClipboard() {
      if (!this.clipboardId.trim()) {
        this.error = 'Please enter a clipboard ID';
        return;
      }
      
      this.$router.push(`/${this.clipboardId.trim()}`);
    },
    
    loadClipboardHistory() {
      this.clipboardHistory = historyService.getHistory();
    },
    
    accessHistoryClipboard(clipboardId) {
      this.$router.push(`/${clipboardId}`);
    },
    
    removeFromHistory(clipboardId) {
      historyService.removeFromHistory(clipboardId);
      this.loadClipboardHistory(); // Refresh the list
    },
    
    clearAllHistory() {
      if (confirm('Are you sure you want to clear all clipboard history?')) {
        historyService.clearHistory();
        this.loadClipboardHistory(); // Refresh the list
      }
    },
    
    formatRelativeTime(isoString) {
      return historyService.formatRelativeTime(isoString);
    }
  }
}
</script>

<style scoped>
.home {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.container {
  background-color: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.hero-section {
  text-align: center;
  margin-bottom: 40px;
}

.hero-section h1 {
  color: #2c3e50;
  font-weight: 700;
  font-size: 3rem;
  margin-bottom: 15px;
  background: linear-gradient(135deg, #42b983, #2c3e50);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-description {
  font-size: 1.3rem;
  color: #666;
  margin-bottom: 40px;
  line-height: 1.6;
}

.feature-highlights {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 30px;
}

.feature-item {
  text-align: center;
  padding: 25px;
  background: #f8f9fa;
  border-radius: 10px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: 15px;
}

.feature-item h3 {
  color: #2c3e50;
  font-size: 1.3rem;
  margin-bottom: 10px;
  font-weight: 600;
}

.feature-item p {
  color: #666;
  line-height: 1.5;
}

.main-actions {
  margin-bottom: 50px;
  padding: 40px;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  border: 2px solid #e0e0e0;
}

.feature-highlights-section {
  text-align: center;
  margin: 50px 0;
  padding: 40px;
  background: #fafbfc;
  border-radius: 12px;
}

h2 {
  color: #2c3e50;
  font-size: 1.6rem;
  margin-bottom: 20px;
  font-weight: 600;
  text-align: center;
}

h3 {
  color: #2c3e50;
  font-size: 1.2rem;
  margin-bottom: 10px;
  font-weight: 600;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 25px;
}

.quick-links {
  text-align: center;
  margin-top: 40px;
  padding-top: 40px;
  border-top: 2px solid #f0f0f0;
}

.links-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  margin-top: 30px;
}

.link-card {
  display: block;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  padding: 30px 25px;
  text-decoration: none;
  color: inherit;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.link-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  border-color: #42b983;
  text-decoration: none;
  color: inherit;
}

.link-icon {
  font-size: 2.5rem;
  margin-bottom: 15px;
}

.link-card h3 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.2rem;
  font-weight: 600;
}

.link-card p {
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.form-control {
  padding: 15px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: #fafafa;
}

.form-control:focus {
  border-color: #42b983;
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.1);
  background: white;
}

.textarea-control {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

button {
  padding: 15px 25px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

button:active {
  transform: translateY(0);
}

.create-btn {
  background: linear-gradient(135deg, #42b983, #36996b);
  color: white;
  border: none;
}

.create-btn:hover {
  background: linear-gradient(135deg, #36996b, #2e8659);
}

.access-btn {
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
}

.access-btn:hover {
  background: linear-gradient(135deg, #2980b9, #2471a3);
}

.or-divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 40px 0;
  color: #999;
  font-weight: 500;
}

.or-divider::before,
.or-divider::after {
  content: '';
  flex: 1;
  border-bottom: 2px solid #f0f0f0;
}

.or-divider span {
  padding: 0 20px;
  background: white;
  border-radius: 20px;
  border: 2px solid #f0f0f0;
}

/* Clipboard History Styles */
.clipboard-history {
  margin: 40px 0;
  padding: 30px;
  background: linear-gradient(135deg, #fafbfc, #f8f9fa);
  border-radius: 12px;
  border: 2px solid #e9ecef;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.history-header h2 {
  margin: 0;
  text-align: left;
}

.clear-history-btn {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.2);
}

.clear-history-btn:hover {
  background: linear-gradient(135deg, #c82333, #bd2130);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
}

.history-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.history-item {
  display: flex;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 10px;
  border: 2px solid #e9ecef;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: relative;
}

.history-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  border-color: #42b983;
}

.history-icon {
  font-size: 2.2rem;
  margin-right: 15px;
  flex-shrink: 0;
}

.history-content {
  flex: 1;
  min-width: 0; /* Allow content to shrink */
}

.history-title {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.85rem;
  color: #666;
}

.visit-time,
.visit-count {
  display: flex;
  align-items: center;
  gap: 6px;
}

.meta-icon {
  font-size: 0.8rem;
  opacity: 0.8;
}

.remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  color: #6c757d;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0;
  transform: scale(0.8);
}

.history-item:hover .remove-btn {
  opacity: 1;
  transform: scale(1);
}

.remove-btn:hover {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
  transform: scale(1.1);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-section h1 {
    font-size: 2.5rem;
  }
  
  .hero-description {
    font-size: 1.1rem;
  }
  
  .feature-highlights {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .links-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .container {
    padding: 30px 20px;
  }
  
  .history-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .history-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .clear-history-btn {
    align-self: center;
    width: fit-content;
  }
}

@media (max-width: 480px) {
  .home {
    padding: 10px;
  }
  
  .container {
    padding: 20px 15px;
  }
  
  .hero-section h1 {
    font-size: 2rem;
  }
  
  .hero-description {
    font-size: 1rem;
  }
  
  .feature-item {
    padding: 20px;
  }
  
  .link-card {
    padding: 25px 20px;
  }
  
  button {
    padding: 12px 20px;
    font-size: 15px;
  }
  
  .form-control {
    padding: 12px 15px;
    font-size: 15px;
  }
  
  .clipboard-history {
    padding: 20px 15px;
    margin: 30px 0;
  }
  
  .history-item {
    padding: 15px;
  }
  
  .history-icon {
    font-size: 1.8rem;
    margin-right: 12px;
  }
  
  .history-title {
    font-size: 1rem;
  }
  
  .history-meta {
    font-size: 0.8rem;
  }
}
</style> 